// Perspective ⇄ Orthographic 切替時に「画面上の見た目サイズ」を一致させる計算。
// 既存実装には共有 bounding sphere がないため、現 camera の distance / fov だけで完結させる。
// 詳細は execplans/2026-05-20-stl-viewer-2d-3d-toggle.md §1 設計判断を参照。

const MIN_SAFE_VALUE = 1e-6;

const degToRad = (deg: number): number => (deg * Math.PI) / 180;

/**
 * Perspective camera の (fov, distance) から、同じ visible height を持つ
 * Orthographic camera の zoom を算出する。
 * visibleHeight = 2 * distance * tan(fov / 2)
 * zoom = canvasHeight / visibleHeight
 */
export const calcOrthoZoomFromPerspective = (
  fovDeg: number,
  distance: number,
  canvasHeight: number,
): number => {
  const halfFovRad = degToRad(fovDeg) / 2;
  const tanHalfFov = Math.tan(halfFovRad);
  const visibleHeight = 2 * distance * tanHalfFov;
  if (!Number.isFinite(visibleHeight) || visibleHeight < MIN_SAFE_VALUE) {
    return 1;
  }
  return canvasHeight / visibleHeight;
};

/**
 * Orthographic camera の zoom から、同じ visible height を持つ
 * Perspective camera の distance を逆算する。
 */
export const calcPerspectiveDistanceFromOrtho = (
  fovDeg: number,
  zoom: number,
  canvasHeight: number,
): number => {
  if (!Number.isFinite(zoom) || zoom < MIN_SAFE_VALUE) {
    return 1;
  }
  const halfFovRad = degToRad(fovDeg) / 2;
  const tanHalfFov = Math.tan(halfFovRad);
  if (!Number.isFinite(tanHalfFov) || tanHalfFov < MIN_SAFE_VALUE) {
    return 1;
  }
  const visibleHeight = canvasHeight / zoom;
  return visibleHeight / (2 * tanHalfFov);
};
